"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePeriodeStore } from "@/hooks/use-periode";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  FileDown,
  Loader2,
  RefreshCw,
  Search,
  Settings,
  Upload,
  Users,
  CheckCircle2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api-client";
import { toast } from "sonner";
import Cookies from "js-cookie";
import { postSyncKinerja } from "./api/post-sync-kinerja";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import coffeeLottieUrl from "@/assets/Coffee love.lottie?url";
import { FilterCombobox } from "@/components/ui/filter-combobox";
import { Filter, XCircle } from "lucide-react";

const baseUrl = import.meta.env.VITE_API_BASE_URL;

// --- DUMMY DATA ---
const dummyFaculties = [
  "Fakultas Teknik dan Kejuruan",
  "Fakultas Ekonomi",
  "Fakultas Hukum dan Ilmu Sosial",
  "Fakultas Ilmu Pendidikan",
  "Fakultas Bahasa dan Seni",
  "Fakultas Matematika dan Ilmu Pengetahuan Alam",
  "Fakultas Olahraga dan Kesehatan",
  "Fakultas Kedokteran",
];

const monthsOdd = [
  { value: "07", label: "Juli" },
  { value: "08", label: "Agustus" },
  { value: "09", label: "September" },
  { value: "10", label: "Oktober" },
  { value: "11", label: "November" },
  { value: "12", label: "Desember" },
];

const monthsEven = [
  { value: "01", label: "Januari" },
  { value: "02", label: "Februari" },
  { value: "03", label: "Maret" },
  { value: "04", label: "April" },
  { value: "05", label: "Mei" },
  { value: "06", label: "Juni" },
];

import { fetchDosenRemun } from "./api/get-dosen-remun";
import { useAuth } from "@/hooks/use-auth";
import { ROLES } from "@/config/roles";
import { TableLoader } from "@/components/ui/table-loader";
import { ProtectRole } from "@/components/ProtectRole";

export default function RemunDosenPage() {
  const { user } = useAuth();

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const pageParam = searchParams.get("page");
  const limitParam = searchParams.get("limit");
  const searchParam = searchParams.get("search") || "";
  const statusPegawaiParam = searchParams.get("id_status_pegawai") || "all";
  const statusSekarangParam = searchParams.get("id_status_sekarang") || "all";

  const isAdminFakultas = user?.role === "admin_fakultas";
  const formattedFakultasId = user?.master_fakultas_id
    ? String(user.master_fakultas_id).padStart(2, "0")
    : "all";
  const fktKodeParam = isAdminFakultas
    ? formattedFakultasId
    : searchParams.get("fkt_kode") || "all";

  const prdKodeParam = searchParams.get("prd_kode") || "all";
  const statusValidasiParam = searchParams.get("status_validasi") || "all";

  const page = pageParam ? parseInt(pageParam, 10) : 1;
  const limit = limitParam ? parseInt(limitParam, 10) : 10;

  const [filterNama, setFilterNama] = useState(searchParam);
  const [debouncedSearch, setDebouncedSearch] = useState(searchParam);
  const [progressLevel, setProgressLevel] = useState<"fakultas" | "dosen">(
    "fakultas",
  );

  const { getYearAndSemester } = usePeriodeStore();
  const { year, semester, periodeId } = getYearAndSemester();

  const updateParams = (updates: Record<string, string>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      return next;
    });
  };

  const setPage = (newPage: number) => {
    updateParams({ page: newPage.toString() });
  };

  const setLimit = (newLimit: number) => {
    updateParams({ limit: newLimit.toString(), page: "1" });
  };

  // Sinkronisasi jika URL berubah (misal tombol Back dari browser)
  useEffect(() => {
    setFilterNama(searchParam);
    setDebouncedSearch(searchParam);
  }, [searchParam]);

  // Debounce filterNama
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filterNama);
      if (filterNama !== searchParam) {
        updateParams({ search: filterNama, page: "1" }); // Reset page to 1 when search changes
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [filterNama, searchParam]);

  const { data: validationDosenData, isLoading: isLoadingValidationDosen } =
    useQuery({
      queryKey: ["validation-status-dosen", { year, semester }],
      queryFn: async () => {
        const baseUrl = import.meta.env.VITE_API_BASE_URL;
        return apiClient<any>(
          `${baseUrl}/api/v1/validation-status/dosen?year=${year}&semester=${semester}`,
        );
      },
      enabled: !!year && !!semester,
    });

  const dosenValidationMap = useMemo(() => {
    const map = new Map<string, string>();
    if (validationDosenData?.datas) {
      validationDosenData.datas.forEach((item: any) => {
        map.set(item.niu, item.status);
      });
    }
    return map;
  }, [validationDosenData]);

  const filteredNius = useMemo(() => {
    if (statusValidasiParam === "all") return undefined;
    if (!validationDosenData?.datas) return undefined;

    const matched = validationDosenData.datas
      .filter((item: any) => item.status === statusValidasiParam)
      .map((item: any) => item.niu);

    return matched.length > 0 ? matched.join(",") : "none";
  }, [validationDosenData, statusValidasiParam]);

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "dosen-remun",
      {
        year,
        semester,
        periodeID: periodeId,
        page,
        limit,
        search: debouncedSearch,
        id_status_pegawai:
          statusPegawaiParam !== "all" ? statusPegawaiParam : undefined,
        id_status_sekarang:
          statusSekarangParam !== "all" ? statusSekarangParam : undefined,
        fkt_kode: fktKodeParam !== "all" ? fktKodeParam : undefined,
        prd_kode: prdKodeParam !== "all" ? prdKodeParam : undefined,
        nius: filteredNius !== "all" ? filteredNius : undefined,
      },
    ],
    queryFn: fetchDosenRemun,
    enabled:
      !!year &&
      !!semester &&
      !!periodeId &&
      (statusValidasiParam === "all" || filteredNius !== undefined),
  });

  const { data: fakultasData, isLoading: isLoadingFakultas } = useQuery({
    queryKey: ["master-fakultas-all"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(
        `${baseUrl}/api/v1/master-fakultas?page=1&limit=1000`,
      );
    },
  });

  const { data: jurusanData, isLoading: isLoadingJurusan } = useQuery({
    queryKey: ["master-jurusan-all"],
    queryFn: async () => {
      const baseUrl = import.meta.env.VITE_API_BASE_URL;
      return apiClient<any>(
        `${baseUrl}/api/v1/master-jurusan?page=1&limit=1000`,
      );
    },
  });

  const { data: facultyStatsResponse, isLoading: isLoadingFacultyStats } =
    useQuery({
      queryKey: ["faculty-stats", { year, semester }],
      queryFn: async () => {
        const baseUrl = import.meta.env.VITE_API_BASE_URL;
        return apiClient<any>(
          `${baseUrl}/api/v1/validation-status/faculty-stats?year=${year}&semester=${semester}`,
        );
      },
      enabled: !!year && !!semester,
    });

  const facultyStats = useMemo(() => {
    if (!facultyStatsResponse?.data) return [];
    let stats = facultyStatsResponse.data;

    // Filter jika admin fakultas hanya melihat fakultasnya sendiri
    if (isAdminFakultas && formattedFakultasId !== "all") {
      stats = stats.filter((s: any) => s.fkt_kode === formattedFakultasId);
    }

    // Filter item yang kosong (fkt_kode kosong atau total dosen 0)
    return stats.filter((s: any) => s.total_dosen > 0);
  }, [facultyStatsResponse, isAdminFakultas, formattedFakultasId]);

  const filteredJurusan = useMemo(() => {
    const allJurusan = jurusanData?.datas || [];
    if (fktKodeParam === "all") return allJurusan;
    return allJurusan.filter((j: any) => j.kode_fakultas === fktKodeParam);
  }, [jurusanData, fktKodeParam]);

  const syncMutation = useMutation({
    mutationFn: postSyncKinerja,
    onMutate: () => {
      setOpenSyncConfirm(false);
      setOpenSyncLoading(true);
      toast.loading("Menarik data kinerja...", {
        id: "sync-kinerja",
        position: "bottom-right",
      });
    },
    onSuccess: (resData) => {
      setOpenSyncLoading(false);
      toast.success(
        resData.message ||
          "Proses penarikan data evidence telah selesai dieksekusi",
        {
          id: "sync-kinerja",
        },
      );
      // Optionally invalidate queries
      queryClient.invalidateQueries({ queryKey: ["dosen-remun"] });
    },
    onError: (error: any) => {
      setOpenSyncLoading(false);
      toast.error(error?.message || "Gagal menarik data evidence", {
        id: "sync-kinerja",
      });
    },
  });

  // --- LOGIKA FORMATTING DATA DARI API ---
  const formattedData = useMemo(() => {
    if (!data || !data.datas) return [];

    const now = new Date();
    const currentMonthKey = `${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, "0")}`;

    return data.datas.map((dosen: any) => {
      const dataBulan = dosen.DataPerBulan || dosen.data_per_bulan || {};
      const months = Object.keys(dataBulan);

      let currentMonthData = dataBulan[currentMonthKey];
      if (!currentMonthData && months.length > 0) {
        currentMonthData = dataBulan[months[months.length - 1]];
      }
      currentMonthData = currentMonthData || {};

      const nominal =
        currentMonthData.rupiah_bersih_30 ||
        currentMonthData.RupiahBersih30 ||
        0;
      let totalRupiah = Number(nominal);

      const nama = dosen.Nama || dosen.nama || "-";
      const niu = dosen.NIU || dosen.niu;
      const activeJabatan = currentMonthData?.jabatan_aktif?.[0];
      const fakultas =
        dosen?.unit?.fakultas ||
        dosen?.Unit?.Fakultas ||
        activeJabatan?.uk_nama ||
        "-";
      const jurusan =
        dosen?.unit?.prodi || dosen?.unit?.jurusan || dosen?.Unit?.Prodi || "-";
      const cluster =
        currentMonthData.CLuster ||
        currentMonthData.Cluster ||
        currentMonthData.cluster ||
        "-";

      const jab_fungsional =
        currentMonthData.jabatan_fungsional ||
        currentMonthData.JabatanFungsional ||
        "-";
      const poin_fungsional =
        currentMonthData.nilai_fungsional ||
        currentMonthData.NilaiFungsional ||
        0;
      const jab_struktural =
        currentMonthData.jabatan_struktural ||
        currentMonthData.JabatanStruktural ||
        "-";
      const poin_struktural =
        currentMonthData.nilai_struktural ||
        currentMonthData.NilaiStruktural ||
        0;
      const status_pegawai =
        currentMonthData.status_pegawai ||
        currentMonthData.StatusPegawai ||
        "-";
      const status_sekarang =
        currentMonthData.status_sekarang ||
        currentMonthData.StatusSekarang ||
        "-";

      const rawStatus = dosenValidationMap.get(niu) || "belum_divalidasi";

      let displayStatus = "-";

      if (rawStatus === "valid") {
        displayStatus = "Valid";
      } else if (rawStatus === "belum_divalidasi_dosen") {
        displayStatus = "Belum Divalidasi Dosen";
      } else if (rawStatus === "belum_divalidasi_admin_fakultas") {
        displayStatus = "Belum Divalidasi Fakultas";
      } else if (rawStatus === "belum_divalidasi_admin_univ") {
        displayStatus = "Belum Divalidasi Univ";
      } else {
        // Misal ada nilai lain, fallback ke format judul
        displayStatus = rawStatus
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");
      }

      return {
        id: niu,
        nama: nama,
        jurusan: jurusan,
        fakultas: fakultas,
        cluster: cluster,
        jab_fungsional: jab_fungsional,
        poin_fungsional: poin_fungsional,
        jab_struktural: jab_struktural,
        poin_struktural: poin_struktural,
        status_dosen:
          status_sekarang !== "-"
            ? `${status_pegawai} (${status_sekarang})`
            : status_pegawai,
        status_validasi: displayStatus,
        raw_status_validasi: rawStatus,
        total_rupiah: totalRupiah,
        is_penelitian: Boolean(dosen.is_penelitian ?? dosen.IsPenelitian),
        is_pengabdian: Boolean(dosen.is_pengabdian ?? dosen.IsPengabdian),

        jab_fungsional_format: `${jab_fungsional} (${poin_fungsional} Poin)`,
        jab_struktural_format:
          poin_struktural > 0
            ? `${jab_struktural} (${poin_struktural} Poin)`
            : "-",
        total_rupiah_formatted: new Intl.NumberFormat("id-ID", {
          style: "currency",
          currency: "IDR",
          minimumFractionDigits: 0,
        }).format(totalRupiah),
      };
    });
  }, [data, dosenValidationMap]);

  const totalData = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.pages || undefined;
  const hasMore = totalPages
    ? page < totalPages
    : formattedData.length === limit;

  // --- STATE UNTUK EXPORT ---
  const [openExport, setOpenExport] = useState(false);
  const [exportType, setExportType] = useState("30");
  const [exportFaculty, setExportFaculty] = useState("");
  const [exportMonth, setExportMonth] = useState("");
  const [openSyncConfirm, setOpenSyncConfirm] = useState(false);
  const [openSyncLoading, setOpenSyncLoading] = useState(false);

  // --- STATE UNTUK EXPORT 70 PROGRESS ---
  const [openExport70Loading, setOpenExport70Loading] = useState(false);
  const [export70Progress, setExport70Progress] = useState(0);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (openExport70Loading) {
      interval = setInterval(() => {
        setExport70Progress((prev) => {
          if (prev >= 95) return prev;
          const increment = Math.max(0.5, (95 - prev) * 0.05);
          return Math.min(95, prev + increment);
        });
      }, 500);
    } else {
      setExport70Progress(0);
    }
    return () => clearInterval(interval);
  }, [openExport70Loading]);

  const monthOptions = semester === 1 ? monthsOdd : monthsEven;

  // --- STATE UNTUK IMPORT SERDOS ---
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [openImportSerdos, setOpenImportSerdos] = useState(false);
  const [importSerdosMonth, setImportSerdosMonth] = useState("");

  const importRealisasiInputRef = useRef<HTMLInputElement>(null);
  const [openImportRealisasi, setOpenImportRealisasi] = useState(false);
  const [importRealisasiMonth, setImportRealisasiMonth] = useState("");

  const importRealisasiMutation = useMutation({
    mutationFn: async (file: File) => {
      const activeToken = Cookies.get("auth_token");
      const formData = new FormData();
      formData.append("file", file);

      // Construct year_month (e.g. 2026_05)
      let importYear = Number(year);
      if (Number(semester) === 2 && Number(importRealisasiMonth) <= 6) {
        importYear += 1;
      }
      const monthParam = `${importYear}_${importRealisasiMonth}`;

      const params = new URLSearchParams({
        year: year.toString(),
        semester: semester.toString(),
        month: monthParam,
      });

      const res = await fetch(
        `${baseUrl}/api/v1/dosen-remun/upload-rupiah-realisasi-30?${params.toString()}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${activeToken}`,
          },
          body: formData,
        },
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Gagal mengunggah file realisasi 30%");
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success("File Realisasi 30% berhasil diunggah!");
      queryClient.invalidateQueries({ queryKey: ["dosen-remun"] });
      setOpenImportRealisasi(false);
      setImportRealisasiMonth("");
      if (importRealisasiInputRef.current)
        importRealisasiInputRef.current.value = "";
    },
    onError: (err: any) => {
      toast.error(`Gagal mengunggah file: ${err.message}`);
      if (importRealisasiInputRef.current)
        importRealisasiInputRef.current.value = "";
    },
  });

  const handleImportRealisasiClick = () => {
    if (!importRealisasiMonth) {
      toast.error("Pilih bulan terlebih dahulu");
      return;
    }
    const file = importRealisasiInputRef.current?.files?.[0];
    if (!file) {
      toast.error("Pilih file excel terlebih dahulu");
      return;
    }
    importRealisasiMutation.mutate(file);
  };

  const importSerdosMutation = useMutation({
    mutationFn: async (file: File) => {
      const activeToken = Cookies.get("auth_token");
      const formData = new FormData();
      formData.append("file", file);

      // Construct year_month (e.g. 2026_05)
      let importYear = Number(year);
      if (Number(semester) === 2 && Number(importSerdosMonth) <= 6) {
        importYear += 1;
      }
      const monthParam = `${importYear}_${importSerdosMonth}`;

      const params = new URLSearchParams({
        year: year.toString(),
        semester: semester.toString(),
        month: monthParam,
      });

      const res = await fetch(
        `${baseUrl}/api/v1/dosen-remun/upload-serdos?${params.toString()}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${activeToken}`,
          },
          body: formData,
        },
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Gagal mengunggah file serdos");
      }
      return res.json();
    },
    onSuccess: () => {
      toast.success("File Serdos berhasil diunggah!");
      queryClient.invalidateQueries({ queryKey: ["dosen-remun"] });
      setOpenImportSerdos(false);
      setImportSerdosMonth("");
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    onError: (err: any) => {
      toast.error(`Gagal mengunggah file: ${err.message}`);
      if (fileInputRef.current) fileInputRef.current.value = "";
    },
  });

  const handleImportSerdosClick = () => {
    if (!importSerdosMonth) {
      toast.error("Pilih bulan terlebih dahulu");
      return;
    }
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      toast.error("Pilih file excel terlebih dahulu");
      return;
    }
    importSerdosMutation.mutate(file);
  };

  const handleExport = () => {
    const params = new URLSearchParams();
    if (year) params.append("year", year.toString());
    if (semester) params.append("semester", semester.toString());
    if (periodeId) params.append("periodeID", periodeId.toString());

    if (exportType === "30" && exportMonth) {
      const exportYear =
        Number(semester) === 2 ? Number(year) + 1 : Number(year);
      params.append("month", `${exportYear}_${exportMonth}`);
    }

    if (exportType && exportType !== "70") params.append("type", exportType);
    if (exportFaculty && exportFaculty !== "all")
      params.append("fakultas", exportFaculty);

    let endpoint = "/api/v1/dosen-remun/export";
    if (exportType === "70") {
      endpoint = "/api/v1/dosen-remun/export-70";
      setOpenExport(false);
      setOpenExport70Loading(true);
      setExport70Progress(0);
    }

    const exportUrl = `${baseUrl}${endpoint}?${params.toString()}`;

    const loadingId = toast.loading(`Mengekspor data Remun ${exportType}%...`);

    apiClient<Blob>(exportUrl)
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        let filename = `Remun_Dosen_${exportType}_${year}_${semester}`;
        if (exportType === "30" && exportMonth) {
          filename += `_${exportMonth}`;
        }
        filename += `.csv`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Data berhasil diekspor!", { id: loadingId });

        if (exportType === "70") {
          setExport70Progress(100);
          setTimeout(() => setOpenExport70Loading(false), 800);
        }
      })
      .catch((err) => {
        console.error("Export error:", err);
        toast.error("Gagal mengekspor data: " + err.message, { id: loadingId });
        if (exportType === "70") {
          setOpenExport70Loading(false);
        }
      })
      .finally(() => {
        if (exportType !== "70") {
          setOpenExport(false);
        }
      });
  };

  return (
    <div className="container mx-auto py-8">
      <Card className="shadow-xl border-slate-200/60 overflow-hidden bg-white/50 backdrop-blur-xl rounded-xl">
        <CardHeader className="p-6 sm:p-8 border-b border-slate-100">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="text-2xl font-bold flex items-center gap-2 text-slate-900">
                <Users className="w-6 h-6 text-primary/80" />
                Data Remunerasi Dosen
              </CardTitle>
              <CardDescription className="text-slate-500 mt-1.5 text-base">
                Rekapitulasi perhitungan remunerasi dosen berdasarkan data bulan
                aktif.
              </CardDescription>
            </div>
            <div className="flex items-center gap-4">
              {(isLoading || isFetching) && (
                <div className="flex items-center text-sm text-primary bg-primary/10 px-3 py-1.5 rounded-full">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Memuat data...
                </div>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    className="bg-white text-slate-700 border-slate-200 hover:bg-slate-50 transition-colors gap-2 shadow-sm font-semibold rounded-lg"
                  >
                    {syncMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    ) : (
                      <Settings className="w-4 h-4" />
                    )}
                    {syncMutation.isPending
                      ? "Menarik Data..."
                      : "Aksi Lainnya"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 font-medium">
                  <ProtectRole allowedRoles={[]}>
                    <DropdownMenuItem
                      onClick={() => setOpenSyncConfirm(true)}
                      className="cursor-pointer text-primary focus:text-primary focus:bg-primary/10 py-2.5"
                      disabled={syncMutation.isPending}
                    >
                      <RefreshCw
                        className={`w-4 h-4 mr-2 ${syncMutation.isPending ? "animate-spin" : ""}`}
                      />
                      Tarik Data Evidence
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </ProtectRole>
                  <DropdownMenuItem
                    onClick={() => setOpenExport(true)}
                    className="cursor-pointer py-2.5"
                  >
                    <FileDown className="w-4 h-4 mr-2 text-emerald-600" />
                    Export Rekap
                  </DropdownMenuItem>
                  {user?.role === ROLES.ADMIN_SUPER && (
                    <>
                      <DropdownMenuItem
                        onClick={() => setOpenImportSerdos(true)}
                        className="cursor-pointer py-2.5"
                      >
                        <Upload className="w-4 h-4 mr-2 text-amber-600" />
                        Import Serdos
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => setOpenImportRealisasi(true)}
                        className="cursor-pointer py-2.5"
                      >
                        <Upload className="w-4 h-4 mr-2 text-blue-600" />
                        Import Realisasi 30%
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Dialog Konfirmasi Sinkronisasi Kinerja */}
              <Dialog open={openSyncConfirm} onOpenChange={setOpenSyncConfirm}>
                <DialogContent className="sm:max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-rose-600">
                      <AlertTriangle className="w-5 h-5" />
                      Konfirmasi Penarikan Data Evidence
                    </DialogTitle>
                    <DialogDescription className="pt-2 text-slate-600">
                      Apakah Anda yakin ingin memulai penarikan data evidence
                      dosen? Proses ini mungkin memerlukan waktu beberapa saat
                      untuk menyelesaikan pengambilan data terbaru dari sistem
                      CDC.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter className="gap-2 sm:justify-end mt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setOpenSyncConfirm(false)}
                      disabled={syncMutation.isPending}
                    >
                      Batal
                    </Button>
                    <Button
                      type="button"
                      onClick={() => syncMutation.mutate()}
                      disabled={syncMutation.isPending}
                      className="gap-2 bg-primary hover:bg-primary/90"
                    >
                      {syncMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Memproses...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4" />
                          Ya, Tarik Data
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Dialog Loading Penarikan Evidence */}
              <Dialog open={openSyncLoading} onOpenChange={setOpenSyncLoading}>
                <DialogContent className="sm:max-w-sm flex flex-col items-center justify-center p-8 [&>button.absolute]:hidden">
                  <DialogHeader className="w-full text-center space-y-3">
                    <DialogTitle className="text-center text-xl font-bold text-slate-800">
                      Menarik Data Evidence...
                    </DialogTitle>
                    <DialogDescription className="text-center text-slate-500">
                      Mohon tunggu sebentar, data sedang diproses. Anda bisa
                      menutup popup ini dan proses akan tetap berjalan di latar
                      belakang.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="w-56 h-56 -my-4 relative">
                    <DotLottieReact
                      src={coffeeLottieUrl}
                      loop
                      autoplay
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>

                  <Button
                    variant="outline"
                    className="w-full mt-4 border-slate-200 text-slate-600 hover:bg-slate-50"
                    onClick={() => setOpenSyncLoading(false)}
                  >
                    Tutup & Lanjutkan di Latar
                  </Button>
                </DialogContent>
              </Dialog>

              {/* Dialog Loading Export 70% */}
              <Dialog
                open={openExport70Loading}
                onOpenChange={setOpenExport70Loading}
              >
                <DialogContent className="sm:max-w-sm flex flex-col items-center justify-center p-8 [&>button.absolute]:hidden">
                  <DialogHeader className="w-full text-center space-y-3">
                    <DialogTitle className="text-center text-xl font-bold text-slate-800">
                      Mengekspor Remun 70%...
                    </DialogTitle>
                    <DialogDescription className="text-center text-slate-500">
                      Proses ini memakan waktu yang cukup lama. Mohon tunggu
                      sebentar sambil menikmati kopi virtual ini ☕
                    </DialogDescription>
                  </DialogHeader>

                  <div className="w-56 h-56 -my-4 relative">
                    <DotLottieReact
                      src={coffeeLottieUrl}
                      loop
                      autoplay
                      className="absolute inset-0 w-full h-full"
                    />
                  </div>

                  <div className="w-full mt-2 space-y-2">
                    <div className="flex justify-between text-xs font-semibold text-slate-500">
                      <span>Proses berjalan...</span>
                      <span>{Math.round(export70Progress)}%</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-primary h-2.5 rounded-full transition-all duration-500 ease-out"
                        style={{ width: `${export70Progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full mt-6 border-slate-200 text-slate-600 hover:bg-slate-50"
                    onClick={() => setOpenExport70Loading(false)}
                  >
                    Sembunyikan & Lanjutkan di Latar
                  </Button>
                </DialogContent>
              </Dialog>

              {/* Dialog Export Rekap */}
              <Dialog open={openExport} onOpenChange={setOpenExport}>
                <DialogContent className="sm:max-w-106.25">
                  <DialogHeader>
                    <DialogTitle>Export Data Remunerasi</DialogTitle>
                    <DialogDescription>
                      Pilih format dan filter untuk mengunduh data dalam format
                      Excel.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="grid gap-6 py-4">
                    {/* Tipe Export */}
                    <div className="grid gap-2">
                      <Label htmlFor="export-type">Tipe Remun</Label>
                      <Select value={exportType} onValueChange={setExportType}>
                        <SelectTrigger
                          id="export-type"
                          className="w-full bg-white"
                        >
                          <SelectValue placeholder="Pilih Tipe..." />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          className="w-(--radix-select-trigger-width)"
                        >
                          <SelectItem value="30">Remun 30%</SelectItem>
                          <SelectItem value="70">Remun 70%</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Fakultas */}
                    <div className="grid gap-2">
                      <Label htmlFor="export-faculty">Fakultas</Label>
                      <Select
                        value={exportFaculty}
                        onValueChange={setExportFaculty}
                      >
                        <SelectTrigger
                          id="export-faculty"
                          className="w-full bg-white"
                        >
                          <SelectValue placeholder="Semua Fakultas" />
                        </SelectTrigger>
                        <SelectContent
                          position="popper"
                          className="w-(--radix-select-trigger-width)"
                        >
                          <SelectItem value="all">Semua Fakultas</SelectItem>
                          {dummyFaculties.map((f) => (
                            <SelectItem key={f} value={f}>
                              {f}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Bulan (Hanya jika 30%) */}
                    {exportType === "30" && (
                      <div className="grid gap-2">
                        <Label htmlFor="export-month">Pilih Bulan</Label>
                        <Select
                          value={exportMonth}
                          onValueChange={setExportMonth}
                        >
                          <SelectTrigger
                            id="export-month"
                            className="w-full bg-white"
                          >
                            <SelectValue placeholder="Pilih Bulan..." />
                          </SelectTrigger>
                          <SelectContent
                            position="popper"
                            className="w-(--radix-select-trigger-width)"
                          >
                            {monthOptions.map((m) => (
                              <SelectItem key={m.value} value={m.value}>
                                {m.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  <DialogFooter>
                    <Button
                      onClick={handleExport}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      disabled={exportType === "30" && !exportMonth}
                    >
                      Mulai Unduh Excel
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Dialog Import Serdos */}
              <Dialog
                open={openImportSerdos}
                onOpenChange={setOpenImportSerdos}
              >
                <DialogContent className="sm:max-w-106.25">
                  <DialogHeader>
                    <DialogTitle>Import Data Besaran Serdos</DialogTitle>
                    <DialogDescription>
                      Pilih bulan untuk data yang akan diimpor.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-6 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="import-month">Pilih Bulan</Label>
                      <Select
                        value={importSerdosMonth}
                        onValueChange={setImportSerdosMonth}
                      >
                        <SelectTrigger
                          id="import-month"
                          className="w-full bg-white"
                        >
                          <SelectValue placeholder="Pilih Bulan..." />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          {monthOptions.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="import-file">Pilih File Excel</Label>
                      <Input
                        id="import-file"
                        type="file"
                        ref={fileInputRef}
                        accept=".xlsx, .xls"
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setOpenImportSerdos(false)}
                    >
                      Batal
                    </Button>
                    <Button
                      onClick={handleImportSerdosClick}
                      disabled={importSerdosMutation.isPending}
                    >
                      {importSerdosMutation.isPending && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      {importSerdosMutation.isPending
                        ? "Mengunggah..."
                        : "Upload File"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Dialog Import Realisasi 30% */}
              <Dialog
                open={openImportRealisasi}
                onOpenChange={setOpenImportRealisasi}
              >
                <DialogContent className="sm:max-w-106.25">
                  <DialogHeader>
                    <DialogTitle>Import Data Realisasi 30%</DialogTitle>
                    <DialogDescription>
                      Pilih bulan untuk data yang akan diimpor.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="grid gap-6 py-4">
                    <div className="grid gap-2">
                      <Label htmlFor="import-realisasi-month">
                        Pilih Bulan
                      </Label>
                      <Select
                        value={importRealisasiMonth}
                        onValueChange={setImportRealisasiMonth}
                      >
                        <SelectTrigger
                          id="import-realisasi-month"
                          className="w-full bg-white"
                        >
                          <SelectValue placeholder="Pilih Bulan..." />
                        </SelectTrigger>
                        <SelectContent position="popper">
                          {monthOptions.map((m) => (
                            <SelectItem key={m.value} value={m.value}>
                              {m.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="import-realisasi-file">
                        Pilih File Excel
                      </Label>
                      <Input
                        id="import-realisasi-file"
                        type="file"
                        ref={importRealisasiInputRef}
                        accept=".xlsx, .xls"
                        className="bg-white"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setOpenImportRealisasi(false)}
                    >
                      Batal
                    </Button>
                    <Button
                      onClick={handleImportRealisasiClick}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      disabled={importRealisasiMutation.isPending}
                    >
                      {importRealisasiMutation.isPending && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      {importRealisasiMutation.isPending
                        ? "Mengunggah..."
                        : "Upload File"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 sm:p-8">
          <ProtectRole
            allowedRoles={[
              ROLES.ADMIN_FAKULTAS,
              ROLES.ADMIN_UNIV,
              ROLES.ADMIN_SUPER,
            ]}
          >
            {!isLoadingFacultyStats && facultyStats.length > 0 && (
              <div className="mb-8 p-5 bg-white rounded-xl border border-slate-200 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
                  <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Progress Validasi{" "}
                    {progressLevel === "dosen" ? "Dosen" : "Fakultas"}
                  </h3>
                  <Select
                    value={progressLevel}
                    onValueChange={(val) =>
                      setProgressLevel(val as "fakultas" | "dosen")
                    }
                  >
                    <SelectTrigger className="w-45 h-8 text-xs bg-white">
                      <SelectValue placeholder="Pilih Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fakultas">Level Fakultas</SelectItem>
                      <SelectItem value="dosen">Level Dosen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div
                  className={`grid gap-6 ${facultyStats.length === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"}`}
                >
                  {facultyStats.map((stat: any) => {
                    const validCount =
                      progressLevel === "dosen"
                        ? stat.dosen_valid || 0
                        : stat.valid || 0;
                    const percentage =
                      Math.round((validCount / stat.total_dosen) * 100) || 0;
                    return (
                      <div key={stat.fkt_kode} className="space-y-2">
                        <div className="flex justify-between items-end">
                          <div className="text-xs font-medium text-slate-700 truncate pr-4">
                            {stat.fakultas}
                          </div>
                          <div className="text-xs font-bold text-emerald-600 whitespace-nowrap">
                            {percentage}%
                          </div>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden relative">
                          <div
                            className="bg-emerald-500 h-full rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-[10px] text-slate-500 font-medium">
                          <span>{validCount} Valid</span>
                          <span>{stat.total_dosen} Dosen</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </ProtectRole>

          <div className="mb-8 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <Filter className="w-4 h-4" />
                Filter Data
              </div>
              {(filterNama !== "" ||
                statusPegawaiParam !== "all" ||
                statusSekarangParam !== "all" ||
                fktKodeParam !== "all" ||
                prdKodeParam !== "all" ||
                statusValidasiParam !== "all") && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setFilterNama("");
                    setDebouncedSearch("");
                    updateParams({
                      search: "",
                      id_status_pegawai: "",
                      id_status_sekarang: "",
                      fkt_kode: isAdminFakultas
                        ? user?.master_fakultas_id
                          ? String(user.master_fakultas_id).padStart(2, "0")
                          : ""
                        : "",
                      prd_kode: "",
                      status_validasi: "",
                      page: "1",
                    });
                  }}
                  className="h-8 text-xs text-red-500 hover:text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-3.5 h-3.5 mr-1.5" />
                  Reset Filter
                </Button>
              )}
            </div>

            <div
              className={`flex flex-col gap-4 p-4 rounded-xl transition-all duration-200 border ${
                filterNama !== "" ||
                statusPegawaiParam !== "all" ||
                statusSekarangParam !== "all" ||
                fktKodeParam !== "all" ||
                prdKodeParam !== "all" ||
                statusValidasiParam !== "all"
                  ? "bg-primary/5 border-primary/20 shadow-sm"
                  : "bg-slate-50/50 border-slate-100"
              }`}
            >
              <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* 1. Pencarian */}
                <div className="w-full">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Pencarian
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Ketik nama atau NIP dosen..."
                      value={filterNama}
                      onChange={(e) => setFilterNama(e.target.value)}
                      className="w-full bg-white pl-9 border-slate-200 focus-visible:ring-primary shadow-sm rounded-lg text-sm"
                    />
                  </div>
                </div>

                {/* 2. Status Pegawai */}
                <div className="w-full">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Status Pegawai
                  </label>
                  <FilterCombobox
                    value={statusPegawaiParam}
                    onValueChange={(val) =>
                      updateParams({
                        id_status_pegawai: val === "all" ? "" : val,
                        page: "1",
                      })
                    }
                    options={[
                      { label: "PNS", value: "1" },
                      { label: "CPNS", value: "4" },
                      { label: "PPPK", value: "7" },
                    ]}
                    placeholder="Semua Status Pegawai"
                    searchPlaceholder="Cari status pegawai..."
                    emptyText="Status tidak ditemukan."
                  />
                </div>

                {/* 3. Status Sekarang */}
                <div className="w-full">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Status Sekarang
                  </label>
                  <FilterCombobox
                    value={statusSekarangParam}
                    onValueChange={(val) =>
                      updateParams({
                        id_status_sekarang: val === "all" ? "" : val,
                        page: "1",
                      })
                    }
                    options={[
                      { label: "Tugas Belajar", value: "1" },
                      { label: "Aktif", value: "2" },
                      { label: "Ijin Belajar", value: "3" },
                    ]}
                    placeholder="Semua Status Sekarang"
                    searchPlaceholder="Cari status sekarang..."
                    emptyText="Status tidak ditemukan."
                  />
                </div>

                {/* 4. Fakultas */}
                <div className="w-full">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Fakultas
                  </label>
                  <FilterCombobox
                    value={fktKodeParam}
                    onValueChange={(val) =>
                      updateParams({
                        fkt_kode: val === "all" ? "" : val,
                        prd_kode: "", // Reset prodi if fakultas changes
                        page: "1",
                      })
                    }
                    options={(fakultasData?.datas || []).map((fkt: any) => ({
                      label: fkt.nama_fakultas,
                      value: fkt.kode_fakultas,
                    }))}
                    placeholder={
                      isLoadingFakultas ? "Memuat..." : "Semua Fakultas"
                    }
                    searchPlaceholder="Cari fakultas..."
                    emptyText="Fakultas tidak ditemukan."
                    disabled={isLoadingFakultas || isAdminFakultas}
                  />
                </div>

                {/* 5. Jurusan/Prodi */}
                <div className="w-full">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Jurusan/Prodi
                  </label>
                  <FilterCombobox
                    value={prdKodeParam}
                    onValueChange={(val) =>
                      updateParams({
                        prd_kode: val === "all" ? "" : val,
                        page: "1",
                      })
                    }
                    options={filteredJurusan.map((jur: any) => ({
                      label: jur.nama_jurusan,
                      value: jur.master_jurusan_id,
                    }))}
                    placeholder={
                      isLoadingJurusan ? "Memuat..." : "Semua Jurusan/Prodi"
                    }
                    searchPlaceholder="Cari jurusan/prodi..."
                    emptyText="Jurusan/Prodi tidak ditemukan."
                    disabled={isLoadingJurusan}
                  />
                </div>

                {/* 6. Status Validasi */}
                <div className="w-full">
                  <label className="text-xs font-semibold leading-none mb-2 block text-slate-600">
                    Status Validasi
                  </label>
                  <FilterCombobox
                    placeholder="Semua Status Validasi"
                    value={statusValidasiParam}
                    onValueChange={(val) =>
                      updateParams({ status_validasi: val, page: "1" })
                    }
                    options={[
                      { value: "valid", label: "Valid" },
                      {
                        value: "belum_divalidasi_dosen",
                        label: "Belum Divalidasi Dosen",
                      },
                      {
                        value: "belum_divalidasi_admin_fakultas",
                        label: "Belum Divalidasi Fakultas",
                      },
                      {
                        value: "belum_divalidasi_admin_univ",
                        label: "Belum Divalidasi Univ",
                      },
                    ]}
                    searchPlaceholder="Cari status..."
                    emptyText="Status tidak ditemukan."
                    disabled={isLoadingValidationDosen}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* GRID TABLE */}
          {isError ? (
            <div className="flex h-64 items-center justify-center rounded-md border border-red-200 bg-red-50 text-red-600">
              Terjadi kesalahan saat memuat data.
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              <div className="w-full rounded-xl border border-slate-200/80 overflow-hidden shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] bg-white relative">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-12 min-w-12 max-w-12 text-center border-r font-semibold sticky left-0 z-20 bg-slate-50">
                          No
                        </TableHead>
                        <TableHead className="min-w-65 font-semibold border-r sticky left-12 z-20 bg-slate-50 shadow-[4px_0_12px_rgba(0,0,0,0.02)]">
                          Nama Dosen
                        </TableHead>
                        <TableHead
                          className="min-w-32 font-semibold border-r"
                          title="Penelitian & Pengabdian"
                        >
                          Lit & PkM
                        </TableHead>
                        {/* <TableHead className="min-w-25 font-semibold border-r">
                          Cluster
                        </TableHead> */}
                        <TableHead className="min-w-45 font-semibold border-r">
                          Jurusan
                        </TableHead>
                        <TableHead className="min-w-25 font-semibold border-r">
                          Fakultas
                        </TableHead>
                        <TableHead className="min-w-52.5 font-semibold border-r">
                          Jab. Fungsional
                        </TableHead>
                        <TableHead className="min-w-45 font-semibold border-r">
                          Jab. Struktural
                        </TableHead>
                        <TableHead className="min-w-40 font-semibold border-r">
                          Status
                        </TableHead>
                        <TableHead className="min-w-35 font-semibold border-r sticky right-25 z-20 bg-slate-50 shadow-[-4px_0_12px_rgba(0,0,0,0.02)]">
                          Validasi
                        </TableHead>
                        <TableHead className="w-25 text-center font-semibold sticky right-0 z-20 bg-slate-50">
                          Aksi
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading || isFetching ? (
                        <TableLoader colSpan={10} text="Memuat data dosen..." />
                      ) : formattedData.length > 0 ? (
                        formattedData.map((row: any, idx: number) => (
                          <TableRow
                            key={row.id}
                            className="hover:bg-slate-50 transition-colors group"
                          >
                            <TableCell className="w-12 min-w-12 max-w-12 text-center border-r text-slate-500 sticky left-0 z-10 bg-white group-hover:bg-slate-50 transition-colors">
                              {(page - 1) * limit + idx + 1}
                            </TableCell>
                            <TableCell className="font-medium text-slate-900 border-r sticky left-12 z-10 bg-white group-hover:bg-slate-50 transition-colors shadow-[4px_0_12px_rgba(0,0,0,0.02)]">
                              {row.nama}
                            </TableCell>
                            <TableCell className="border-r py-2">
                              <div className="flex flex-col gap-1">
                                <Badge
                                  variant="outline"
                                  className={`inline-flex items-center gap-1 text-[11px] font-medium py-0.5 px-2 w-fit ${
                                    row.is_penelitian
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                      : "bg-rose-50 text-rose-700 border-rose-200"
                                  }`}
                                >
                                  {row.is_penelitian ? (
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                                  ) : (
                                    <XCircle className="w-3 h-3 text-rose-500 shrink-0" />
                                  )}
                                  <span>Penelitian</span>
                                </Badge>
                                <Badge
                                  variant="outline"
                                  className={`inline-flex items-center gap-1 text-[11px] font-medium py-0.5 px-2 w-fit ${
                                    row.is_pengabdian
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                      : "bg-rose-50 text-rose-700 border-rose-200"
                                  }`}
                                >
                                  {row.is_pengabdian ? (
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600 shrink-0" />
                                  ) : (
                                    <XCircle className="w-3 h-3 text-rose-500 shrink-0" />
                                  )}
                                  <span>Pengabdian</span>
                                </Badge>
                              </div>
                            </TableCell>
                            {/* <TableCell className="border-r">
                              {row.cluster}
                            </TableCell> */}
                            <TableCell className="border-r">
                              {row.jurusan}
                            </TableCell>
                            <TableCell className="border-r">
                              {row.fakultas}
                            </TableCell>
                            <TableCell className="border-r">
                              {row.jab_fungsional}
                            </TableCell>
                            <TableCell className="border-r">
                              {row.jab_struktural}
                            </TableCell>
                            <TableCell className="border-r">
                              {row.status_dosen}
                            </TableCell>
                            <TableCell className="border-r sticky right-25 z-10 bg-white group-hover:bg-slate-50 transition-colors shadow-[-4px_0_12px_rgba(0,0,0,0.02)]">
                              <Badge
                                variant="outline"
                                className={`
                                  ${
                                    row.raw_status_validasi === "valid"
                                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                      : row.raw_status_validasi ===
                                          "belum_divalidasi_dosen"
                                        ? "bg-rose-50 text-rose-700 border-rose-200"
                                        : row.raw_status_validasi ===
                                            "belum_divalidasi_admin_fakultas"
                                          ? "bg-amber-50 text-amber-700 border-amber-200"
                                          : row.raw_status_validasi ===
                                              "belum_divalidasi_admin_univ"
                                            ? "bg-blue-50 text-blue-700 border-blue-200"
                                            : "bg-slate-50 text-slate-700 border-slate-200"
                                  } font-medium
                                `}
                              >
                                {row.status_validasi}
                              </Badge>
                            </TableCell>
                            <TableCell className="p-2 sticky right-0 z-10 bg-white group-hover:bg-slate-50 transition-colors border-l border-slate-100">
                              <Button
                                variant="default"
                                size="sm"
                                className="w-full h-7 px-3 text-xs bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                                onClick={() =>
                                  navigate(`/admin/remun-dosen/${row.id}`)
                                }
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="14"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  className="mr-1.5"
                                >
                                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                                  <circle cx="12" cy="12" r="3" />
                                </svg>
                                Lihat
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            className="h-40 text-center text-slate-500"
                          >
                            Tidak ada data dosen yang ditemukan
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Pagination Controls */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-100">
                <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-start">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-slate-500">
                      Tampilkan
                    </span>
                    <Select
                      value={limit.toString()}
                      onValueChange={(val) => setLimit(Number(val))}
                    >
                      <SelectTrigger className="w-18 bg-white text-sm rounded-lg shadow-sm border-slate-200 focus:ring-primary h-9">
                        <SelectValue placeholder={limit.toString()} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                    <span className="text-sm font-medium text-slate-500">
                      data
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 border-l border-slate-200 pl-4 hidden sm:block">
                    {totalData > 0 ? (
                      <>
                        Menampilkan{" "}
                        <span className="font-semibold text-slate-700">
                          {formattedData.length}
                        </span>{" "}
                        dari{" "}
                        <span className="font-semibold text-slate-700">
                          {totalData}
                        </span>
                      </>
                    ) : (
                      <>
                        Menampilkan{" "}
                        <span className="font-semibold text-slate-700">
                          {formattedData.length}
                        </span>{" "}
                        data
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  <span className="text-sm text-slate-600 font-medium">
                    {totalPages
                      ? `Halaman ${page} dari ${totalPages}`
                      : `Halaman ${page}`}
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg shadow-sm border-slate-200 hover:bg-slate-50 hover:text-primary transition-colors h-9"
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1 || isLoading || isFetching}
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Sebelumnya
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-lg shadow-sm border-slate-200 hover:bg-slate-50 hover:text-primary transition-colors h-9"
                      onClick={() => setPage(page + 1)}
                      disabled={!hasMore || isLoading || isFetching}
                    >
                      Selanjutnya
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
